// Nén ảnh phía client trước khi lưu base64 vào nội dung (bài giảng/khoá học/quiz)
// → tránh phồng localStorage/DB và cảnh báo dung lượng lớn.
// Dùng canvas: thu nhỏ theo chiều rộng/cao tối đa + tái mã hoá JPEG chất lượng cao.

export interface CompressOptions {
  maxWidth?: number;   // chiều rộng tối đa (px)
  maxHeight?: number;  // chiều cao tối đa (px)
  quality?: number;    // 0..1 cho JPEG/WebP
  mimeType?: 'image/jpeg' | 'image/webp' | 'image/png';
}

const DEFAULTS: Required<CompressOptions> = {
  maxWidth: 1280,
  maxHeight: 1280,
  quality: 0.8,
  mimeType: 'image/jpeg',
};

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(r.error || new Error('Không đọc được file'));
    r.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Không tải được ảnh'));
    img.src = src;
  });
}

/**
 * Nén 1 File ảnh → data URL base64 (đã thu nhỏ + nén).
 * - Không phải ảnh (vd PDF) → trả base64 nguyên (không đụng).
 * - Nếu nén không nhỏ hơn bản gốc (ảnh đã nhỏ) → giữ bản nhỏ hơn.
 * - Mọi lỗi → fallback base64 gốc (an toàn, không chặn người dùng).
 */
export async function compressImageFile(file: File, opts: CompressOptions = {}): Promise<string> {
  const o = { ...DEFAULTS, ...opts };
  const original = await readAsDataURL(file);
  if (!file.type || !file.type.startsWith('image/')) return original;     // PDF/khác → nguyên
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') return original; // giữ động/vector
  try {
    const img = await loadImage(original);
    const w0 = img.naturalWidth || img.width;
    const h0 = img.naturalHeight || img.height;
    if (!w0 || !h0) return original;
    const scale = Math.min(1, o.maxWidth / w0, o.maxHeight / h0);
    const w = Math.max(1, Math.round(w0 * scale));
    const h = Math.max(1, Math.round(h0 * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return original;
    // JPEG không có alpha → tô nền trắng tránh nền đen với ảnh PNG trong suốt
    if (o.mimeType === 'image/jpeg') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
    }
    ctx.drawImage(img, 0, 0, w, h);
    const out = canvas.toDataURL(o.mimeType, o.quality);
    return (out && out.length < original.length) ? out : original;
  } catch {
    return original;
  }
}

/** Tiện ích: ước lượng dung lượng (bytes) của data URL base64. */
export function dataUrlBytes(dataUrl: string): number {
  const i = (dataUrl || '').indexOf(',');
  const b64 = i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
  return Math.floor((b64.length * 3) / 4);
}
